import { API_RESPONSE_TYPE } from 'shared/constants'
import { getPasswordStatus, setPassword } from 'services/Api/SecurityPinApi'

// Security PIN, ported from Android/iOS (app-platform/auth AuthServiceImpl): the PIN is
// verified locally against a checksum synced from the server; there is no server-side
// "verify" call. Setting / changing / resetting all go OTP → POST the new PIN.

export const PIN_LENGTH = 4
export const OTP_LENGTH = 6

// Same suffix and encoding as mobile's hashPassword, so the server checksum matches
const HASH_SUFFIX = 'jhgjmbsuiyoilanjjviakv'
// Removed on sign-out by AuthStorage.clear()
const HASH_KEY = 'okc_password_hash'

export class PinNetworkError extends Error {}

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const getLocalHash = (): string => storage()?.getItem(HASH_KEY) || ''
export const hasLocalPin = (): boolean => Boolean(getLocalHash())
const setLocalHash = (hash: string) => {
  try {
    storage()?.setItem(HASH_KEY, hash)
  } catch {}
}

export const hashPin = async (pin: string): Promise<string> => {
  const bytes = new TextEncoder().encode(pin + HASH_SUFFIX)
  const digest = await window.crypto.subtle.digest('SHA-256', bytes)
  let binary = ''
  new Uint8Array(digest).forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return window.btoa(binary)
}

// null = the server could not be reached
const fetchRemoteHash = async (): Promise<{ hash: string } | null> => {
  const response = await getPasswordStatus()
  if (response.type !== API_RESPONSE_TYPE.SUCCESS) {
    return null
  }
  return { hash: response.data?.password_set ? response.data?.checksum || '' : '' }
}

// Best effort (mobile syncPassword): picks up a PIN set or changed on another device. Only
// ever overwrites; a failure leaves the local copy alone.
export const syncPin = async (): Promise<void> => {
  try {
    const remote = await fetchRemoteHash()
    if (remote && remote.hash) {
      setLocalHash(remote.hash)
    }
  } catch {}
}

// Throws PinNetworkError when there is no local copy and the server cannot be asked
export const isPinSet = async (): Promise<boolean> => {
  if (getLocalHash()) {
    return true
  }
  const remote = await fetchRemoteHash()
  if (!remote) {
    throw new PinNetworkError()
  }
  if (!remote.hash) {
    return false
  }
  setLocalHash(remote.hash)
  return true
}

export const verifyPin = async (pin: string): Promise<boolean> => {
  const local = getLocalHash()
  return Boolean(local) && local === (await hashPin(pin))
}

// Throws PinNetworkError if the server did not accept it
export const savePin = async (pin: string): Promise<void> => {
  const response = await setPassword(pin)
  if (response.type !== API_RESPONSE_TYPE.SUCCESS) {
    throw new PinNetworkError()
  }
  setLocalHash(await hashPin(pin))
}

// --- "Payment PIN" preference (individual_user.payment_password_enabled) -----------------
// Module-level holder so the Add Payment dialog can read it synchronously; filled from
// /identity/v3/me and kept in step by the Security page toggle.
let paymentPinEnabled = false
const listeners = new Set<() => void>()

export const getPaymentPinEnabled = () => paymentPinEnabled
export const setPaymentPinEnabled = (enabled?: boolean | null) => {
  const next = Boolean(enabled)
  if (next !== paymentPinEnabled) {
    paymentPinEnabled = next
    listeners.forEach((listener) => listener())
  }
}
export const subscribePaymentPin = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
